<template>
  <div class="recruitment-portal" :style="cssProps">
    <!-- 正常校招首页内容 -->
    <div v-if="subdomainExists" class="portal-main-content">
      <!-- 顶部导航栏 -->
      <header class="portal-header">
        <div class="logo-area">
          <img v-if="portalInfo.logoUrl" :src="portalInfo.logoUrl" alt="Logo" class="h-10 w-auto rounded-full shadow-sm" />
          <span class="company-name">{{ portalInfo.welcomeText || '校园招聘门户' }}</span>
        </div>
        <nav class="portal-nav">
          <a href="javascript:;" class="nav-item" :class="{ active: currentTab === 'home' }" @click="currentTab = 'home'">首页</a>
          <a href="javascript:;" class="nav-item" :class="{ active: currentTab === 'jobs' }" @click="switchTab('jobs')">校招职位</a>
          <a href="javascript:;" class="nav-item" @click="goDashboard">个人中心</a>
          <span v-if="candidateName" class="user-greeting">
            你好，{{ candidateName }}
            <a-button type="text" size="small" class="ml-2 text-red-500" @click="handleLogout">退出</a-button>
          </span>
          <el-button v-else type="primary" size="small" round @click="showLogin = true">学生登录</el-button>
        </nav>
      </header>

      <!-- 巨幕 Banner -->
      <section class="hero-banner">
        <div class="banner-bg" :style="portalInfo.bannerUrl ? { backgroundImage: `url(${portalInfo.bannerUrl})` } : {}"></div>
        <div class="hero-content glass-panel animate-fade-in">
          <h1 class="banner-title">{{ portalInfo.welcomeText || '国药集团 2026 届高校毕业生招聘' }}</h1>
          <p class="banner-subtitle">加入我们，以梦为马，不负韶华</p>
        </div>
      </section>

      <!-- 主要内容区域 (两栏布局) -->
      <main v-if="currentTab === 'home'" class="portal-body grid-layout">
        <!-- 左侧：公告简章 (占2/3) -->
        <section class="announcement-section portal-card">
          <h2 class="section-title text-2xl font-bold text-gray-800 pb-4 border-b mb-6 flex items-center">
            <i class="el-icon-document mr-2 text-theme"></i> 招聘公告
            <el-button type="text" size="medium" class="view-ann-detail-btn" @click="showAnnouncementDetail = true">查看详情</el-button>
          </h2>
          <div class="announcement-content rich-text" v-html="portalInfo.announcement || defaultAnnouncement"></div>
        </section>

        <!-- 右侧：招聘活动 (占1/3) -->
        <section class="events-section portal-card">
          <h2 class="section-title text-2xl font-bold text-gray-800 pb-4 border-b mb-6 flex items-center">
            <i class="el-icon-date mr-2 text-theme"></i> 在招活动
          </h2>
          <div v-if="events.length === 0" class="empty-events text-center py-12 text-gray-400">
            暂无发布中的校招活动
          </div>
          <div v-else class="event-list space-y-4">
            <div v-for="event in events" :key="event.id" class="event-card p-5 rounded-xl border border-gray-100 shadow-hover transition-all duration-300">
              <h3 class="event-title font-bold text-lg text-gray-800 mb-2">{{ event.title }}</h3>
              <div class="event-dates text-gray-500 text-xs mb-3">
                <i class="el-icon-time mr-1"></i> {{ formatTime(event.startTime) }} 至 {{ formatTime(event.endTime) }}
              </div>
              <!-- 测试要求标识 -->
              <div class="event-badges flex space-x-2 mb-4">
                <el-tag v-if="event.needExam === 1" size="mini" type="primary">需要在线考试</el-tag>
                <el-tag v-if="event.needAsmt === 1" size="mini" type="warning">需要性格测评</el-tag>
              </div>
              <el-button type="primary" size="medium" class="w-full theme-btn-grad" @click="handleApply(event)">
                立即报名
              </el-button>
            </div>
          </div>
        </section>
      </main>

      <!-- 职位列表区域 -->
      <main v-else-if="currentTab === 'jobs'" class="portal-body">
        <!-- 筛选与搜索面板 -->
        <section class="filter-panel">
          <div class="filter-container">
            <div class="filter-items">
              <!-- 地区筛选 -->
              <div class="filter-item">
                <span class="filter-label">地区:</span>
                <el-select v-model="selectedFilters.addressId" placeholder="全部地区" clearable @change="loadJobsData" size="medium" style="width: 176px;">
                  <el-option
                    v-for="addr in filters.addresses"
                    :key="addr.id"
                    :label="addr.areaName || addr.deptName"
                    :value="addr.id.toString()"
                  ></el-option>
                </el-select>
              </div>

              <!-- 部门筛选 -->
              <div class="filter-item">
                <span class="filter-label">部门:</span>
                <el-select v-model="selectedFilters.department" placeholder="全部部门" clearable @change="loadJobsData" size="medium" style="width: 176px;">
                  <el-option
                    v-for="dept in filters.departments"
                    :key="dept"
                    :label="dept"
                    :value="dept"
                  ></el-option>
                </el-select>
              </div>

              <!-- 职能筛选 -->
              <div class="filter-item">
                <span class="filter-label">职能:</span>
                <el-select v-model="selectedFilters.functionId" placeholder="全部职能" clearable @change="loadJobsData" size="medium" style="width: 176px;">
                  <el-option
                    v-for="fun in filters.functions"
                    :key="fun.id"
                    :label="fun.name"
                    :value="fun.id"
                  ></el-option>
                </el-select>
              </div>
            </div>

            <div>
              <el-button type="default" size="medium" @click="resetFilters">重置</el-button>
            </div>
          </div>
        </section>

        <!-- 职位展示列表 -->
        <div v-loading="jobsLoading">
          <div v-if="jobs.length === 0" class="empty-jobs text-center py-24 bg-white rounded-2xl shadow-sm text-gray-400">
            <i class="el-icon-search text-5xl mb-4 text-gray-300"></i>
            <p>暂无符合筛选条件的校招职位</p>
          </div>
          <div v-else class="jobs-grid">
            <div v-for="job in jobs" :key="job.id" class="job-card" @click="viewJobDetail(job)">
              <div>
                <div class="job-card-header">
                  <h3 class="job-title">{{ job.jobName }}</h3>
                  <span class="job-salary">
                    {{ formatSalary(job) }}
                  </span>
                </div>
                
                <!-- 职位元信息标签 -->
                <div class="job-meta-tags">
                  <el-tag size="small" type="info" effect="plain">{{ job.department }}</el-tag>
                  <el-tag size="small" type="success" effect="plain" v-if="job.addressDetail">{{ job.addressDetail.areaName }}</el-tag>
                  <el-tag size="small" type="warning" effect="plain">{{ getEducationStr(job.education) }}</el-tag>
                  <el-tag size="small" type="danger" effect="plain">招 {{ job.recruitNumber }} 人</el-tag>
                </div>

                <!-- 职位描述简述 -->
                <div class="job-desc-excerpt">
                  {{ job.jobDescription || '暂无职位描述。' }}
                </div>
              </div>

              <div class="job-card-footer">
                <span class="publish-time">发布于 {{ formatTime(job.createTime) }}</span>
                <el-button type="primary" size="medium" class="theme-btn-grad px-6" @click.stop="handleJobApply(job)">
                  立即投递
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </main>

    <!-- 登录弹窗 (Element UI) -->
    <el-dialog title="学生快捷登录" :visible.sync="showLogin" width="400px" center custom-class="glass-dialog">
      <el-form :model="loginForm" ref="loginForm" label-position="top" @submit.native.prevent="handleLogin">
        <el-form-item label="手机号" prop="mobile">
          <el-input v-model="loginForm.mobile" placeholder="请输入您的手机号" prefix-icon="el-icon-phone"></el-input>
        </el-form-item>
        <el-form-item label="短信验证码" prop="code">
          <div class="flex space-x-2">
            <el-input v-model="loginForm.code" placeholder="请输入验证码" prefix-icon="el-icon-lock" class="flex-grow"></el-input>
            <el-button :disabled="countdown > 0" @click="sendCode" class="w-32">
              {{ countdown > 0 ? `${countdown}秒后重新获取` : '发送验证码' }}
            </el-button>
          </div>
        </el-form-item>
        <div class="mt-6">
          <el-button type="primary" native-type="submit" class="w-full theme-btn-grad" :loading="loginLoading">
            登 录
          </el-button>
        </div>
      </el-form>
    </el-dialog>

    <!-- 职位详情弹窗 (Element UI) -->
    <el-dialog :title="selectedJob.jobName" :visible.sync="showJobDetail" width="680px" custom-class="job-detail-dialog" append-to-body>
      <div v-if="selectedJob.id" class="job-detail-container">
        <!-- 头部职位核心信息 -->
        <div class="job-detail-header">
          <div class="job-detail-title-row">
            <h2 class="job-detail-name">{{ selectedJob.jobName }}</h2>
            <span class="job-detail-salary">{{ formatSalary(selectedJob) }}</span>
          </div>
          <div class="job-detail-tags">
            <el-tag size="small" type="info" effect="plain">{{ selectedJob.department }}</el-tag>
            <el-tag size="small" type="success" effect="plain" v-if="selectedJob.addressDetail">{{ selectedJob.addressDetail.areaName }}</el-tag>
            <el-tag size="small" type="warning" effect="plain">{{ getEducationStr(selectedJob.education) }}</el-tag>
            <el-tag size="small" type="danger" effect="plain">招 {{ selectedJob.recruitNumber }} 人</el-tag>
          </div>
        </div>

        <!-- 职位描述 -->
        <div class="job-detail-section">
          <h3 class="job-detail-sec-title">职位描述</h3>
          <p class="job-detail-text">{{ selectedJob.jobDescription || '暂无描述。' }}</p>
        </div>

        <!-- 职位要求 -->
        <div class="job-detail-section">
          <h3 class="job-detail-sec-title">职位要求</h3>
          <p class="job-detail-text">{{ selectedJob.jobRequirement || '暂无要求。' }}</p>
        </div>

        <!-- 底部投递按钮 -->
        <div class="job-detail-footer">
          <el-button type="default" @click="showJobDetail = false" size="medium">关闭</el-button>
          <el-button type="primary" class="theme-btn-grad px-8" size="medium" @click="handleJobApplyFromDetail">
            立即投递
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 招聘公告详情弹窗 (Element UI) -->
    <el-dialog title="招聘公告详情" :visible.sync="showAnnouncementDetail" width="800px" custom-class="announcement-detail-dialog" append-to-body>
      <div class="announcement-detail-container rich-text" v-html="portalInfo.announcement || defaultAnnouncement"></div>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="showAnnouncementDetail = false" size="medium" class="theme-btn-grad px-6">已阅</el-button>
      </div>
    </el-dialog>
    </div>

    <!-- 新增：子域名不存在或失效时的友好报错页面 -->
    <div v-else class="friendly-error-container">
      <div class="error-card">
        <div class="icon-warning">
          <i class="el-icon-warning"></i>
        </div>
        <h2 class="error-title">校招官网未启用</h2>
        <p class="error-message">
          对不起，您所访问的校园招聘网站不存在或已被系统管理员暂停服务。
        </p>
        <p class="error-sub-message">
          请检查输入的二级域名是否正确，或联系该企业的 HR 确认访问链接。
        </p>
        <div class="error-actions">
          <a href="https://www.zhiyuansupin.com" class="back-home-btn">返回 职远速聘 首页</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { getRecruitmentPortalLayoutApi, postRecruitmentLoginApi, getRecruitmentJobsApi } from '@/api';

  export default {
    name: 'RecruitmentPortal',
    data() {
      return {
        subdomainExists: true,
        portalInfo: {},
        events: [],
        candidateName: localStorage.getItem('CandidateName') || '',
        showLogin: false,
        loginLoading: false,
        countdown: 0,
        loginForm: {
          mobile: '',
          code: '',
        },
        defaultBanner: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1200&q=80',
        defaultAnnouncement: '<h3>欢迎参加校园招聘！</h3><p>请在右侧选择合适的招聘活动进行报名，并按时参加对应的考试和性格测评任务。</p>',
        currentTab: 'home',
        jobs: [],
        jobsLoading: false,
        filters: {
          departments: [],
          addresses: [],
          functions: [],
        },
        selectedFilters: {
          department: '',
          addressId: '',
          functionId: '',
        },
        showJobDetail: false,
        selectedJob: {},
        showAnnouncementDetail: false,
      };
    },
    computed: {
      cssProps() {
        return {
          '--theme-color': this.portalInfo.themeColor || '#1890ff',
        };
      },
    },
    mounted() {
      this.loadPortalData();
    },
    methods: {
      async loadPortalData() {
        // 从 URL 参数或 Host 解析二级域名
        const urlParams = new URLSearchParams(window.location.search);
        const hostname = window.location.hostname;
        const domainParts = hostname.split('.');
        
        const subdomain = 
          this.$route.query.subdomain || 
          urlParams.get('subdomain') || 
          (domainParts.length > 2 && domainParts[0] !== 'www' ? domainParts[0] : null) || 
          'sinopharm2026';
        
        try {
          const res = await getRecruitmentPortalLayoutApi(subdomain);
          if (res && res.code === 0 && res.data && res.data.portal && res.data.portal.subdomain) {
            this.subdomainExists = true;
            this.portalInfo = res.data.portal || {};
            this.events = res.data.events || [];
            if (this.portalInfo.welcomeText) {
              document.title = this.portalInfo.welcomeText;
            }
          } else {
            this.subdomainExists = false;
            document.title = '校园招聘-官网未启用';
          }
        } catch (e) {
          this.subdomainExists = false;
          document.title = '校园招聘-加载失败';
          this.$message.error('加载校招官网装潢失败，请检查网络');
        }
      },
      formatTime(timeStr) {
        if (!timeStr) return '';
        return timeStr.substring(0, 10);
      },
      handleApply(event) {
        const token = localStorage.getItem('Authorization');
        if (!token) {
          this.showLogin = true;
          this.pendingEvent = event;
          return;
        }
        this.$router.push(`/recruitment/apply/${event.id}`);
      },
      goDashboard() {
        const token = localStorage.getItem('Authorization');
        if (!token) {
          this.showLogin = true;
          return;
        }
        this.$router.push('/recruitment/dashboard');
      },
      sendCode() {
        if (!this.loginForm.mobile) {
          this.$message.warning('请输入手机号');
          return;
        }
        this.$message.success('验证码已发送，测试环境下请输入 666666');
        this.countdown = 60;
        const timer = setInterval(() => {
          if (this.countdown > 0) {
            this.countdown--;
          } else {
            clearInterval(timer);
          }
        }, 1000);
      },
      async handleLogin() {
        if (!this.loginForm.mobile || !this.loginForm.code) {
          this.$message.warning('请填写手机号和验证码');
          return;
        }
        this.loginLoading = true;
        const urlParams = new URLSearchParams(window.location.search);
        const subdomain = urlParams.get('subdomain') || window.location.hostname.split('.')[0] || 'sinopharm2026';
        try {
          const res = await postRecruitmentLoginApi({
            mobile: this.loginForm.mobile,
            sms: this.loginForm.code,
            subdomain: subdomain
          });
          if (res && res.code === 0) {
            localStorage.setItem('Authorization', 'Bearer ' + res.data.token);
            localStorage.setItem('CandidateName', res.data.candidateName || this.loginForm.mobile);
            this.candidateName = res.data.candidateName || this.loginForm.mobile;
            this.$message.success('登录成功！');
            this.showLogin = false;
            
            // 如果有悬挂的活动投递，登录后自动前往投递页
            if (this.pendingEvent) {
              const eventId = this.pendingEvent.id;
              this.pendingEvent = null;
              this.$router.push(`/recruitment/apply/${eventId}`);
            }
          } else {
            this.$message.error(res?.message || '登录失败，验证码可能不正确');
          }
        } catch (e) {
          this.$message.error('登录网络请求异常');
        } finally {
          this.loginLoading = false;
        }
      },
      handleLogout() {
        localStorage.removeItem('Authorization');
        localStorage.removeItem('CandidateName');
        this.candidateName = '';
        this.$message.success('退出成功');
        if (this.$route.path !== '/recruitment/portal') {
          this.$router.push('/recruitment/portal');
        }
      },
      switchTab(tab) {
        this.currentTab = tab;
        if (tab === 'jobs') {
          this.loadJobsData();
        }
      },
      async loadJobsData() {
        this.jobsLoading = true;
        const urlParams = new URLSearchParams(window.location.search);
        const subdomain = this.$route.query.subdomain || urlParams.get('subdomain') || window.location.hostname.split('.')[0] || 'sinopharm2026';
        
        try {
          const res = await getRecruitmentJobsApi(subdomain, {
            addressId: this.selectedFilters.addressId || undefined,
            department: this.selectedFilters.department || undefined,
            functionId: this.selectedFilters.functionId || undefined,
          });
          if (res && res.code === 0 && res.data) {
            this.jobs = res.data.jobs || [];
            this.filters = res.data.filters || { departments: [], addresses: [], functions: [] };
          }
        } catch (e) {
          this.$message.error('加载校招职位失败');
        } finally {
          this.jobsLoading = false;
        }
      },
      resetFilters() {
        this.selectedFilters = { department: '', addressId: '', functionId: '' };
        this.loadJobsData();
      },
      handleJobApply(job) {
        if (this.events.length === 0) {
          this.$message.warning('目前暂无进行中的校招活动，无法申请。');
          return;
        }
        
        // If there's only one event, go directly to that event's apply page.
        if (this.events.length === 1) {
          this.handleApply(this.events[0]);
          return;
        }
        
        // If there are multiple events, let the candidate select which event they want to apply to.
        this.$confirm('请选择您要参与的招聘活动以提交简历：', '选择投递活动', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        }).then(() => {
          this.handleApply(this.events[0]);
        }).catch(() => {});
      },
      formatSalary(job) {
        if (job.salaryType === 2) {
          return '面议';
        }
        const lowest = job.salaryLowest ? (job.salaryLowest / 1000).toFixed(0) : '0';
        const highest = job.salaryHighest ? (job.salaryHighest / 1000).toFixed(0) : '0';
        if (job.salaryType === 0) {
          const count = job.salaryCount || 12;
          return `${lowest}K-${highest}K · ${count}薪`;
        } else if (job.salaryType === 1) {
          const lowestY = job.salaryLowest ? (job.salaryLowest / 10000).toFixed(0) : '0';
          const highestY = job.salaryHighest ? (job.salaryHighest / 10000).toFixed(0) : '0';
          return `${lowestY}万-${highestY}万 / 年`;
        }
        return '面议';
      },
      getEducationStr(eduValue) {
        const eduMap = {
          1: '大专及以下',
          2: '大专',
          3: '本科',
          4: '硕士',
          5: '博士',
        };
        return eduMap[eduValue] || '不限';
      },
      viewJobDetail(job) {
        this.selectedJob = job;
        this.showJobDetail = true;
      },
      handleJobApplyFromDetail() {
        this.showJobDetail = false;
        this.handleJobApply(this.selectedJob);
      },
    },
  };
</script>

<style scoped>
  .recruitment-portal {
    background-color: #f7f9fc;
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  .portal-header {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    position: sticky;
    top: 0;
    z-index: 100;
    border-bottom: 1px solid rgba(226, 232, 240, 0.8);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 40px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }

  .logo-area {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .company-name {
    font-weight: 700;
    font-size: 20px;
    color: #1a202c;
    letter-spacing: -0.5px;
  }

  .portal-nav {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .nav-item {
    color: #4a5568;
    text-decoration: none;
    font-weight: 600;
    font-size: 15px;
    transition: all 0.2s ease;
    padding: 6px 12px;
    border-radius: 8px;
  }
  .nav-item:hover, .nav-item.active {
    color: var(--theme-color);
    background: rgba(24, 144, 255, 0.05);
  }

  .user-greeting {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #4a5568;
    font-size: 14px;
    font-weight: 500;
  }

  .hero-banner {
    position: relative;
    height: 380px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .banner-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    background-image: linear-gradient(135deg, var(--theme-color, #1890ff) 0%, #001f3f 100%);
    filter: brightness(0.65);
    transition: transform 10s ease-out;
  }
  .hero-banner:hover .banner-bg {
    transform: scale(1.05);
  }

  .hero-content {
    position: relative;
    z-index: 10;
    padding: 32px 48px;
    max-width: 800px;
    width: 90%;
    text-align: center;
    box-sizing: border-box;
  }

  .banner-title {
    font-size: 36px;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 1px;
    margin-top: 0;
    margin-bottom: 12px;
    line-height: 1.3;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .banner-subtitle {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.95);
    font-weight: 500;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  .glass-panel {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 20px;
    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.15);
  }

  .portal-body {
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 24px;
  }

  .grid-layout {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 32px;
  }

  .portal-card {
    background: #ffffff;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(226, 232, 240, 0.8);
  }

  .section-title {
    font-size: 24px;
    font-weight: 700;
    color: #1a202c;
    border-bottom: 1px solid #edf2f7;
    padding-bottom: 16px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
  }

  .text-theme {
    color: var(--theme-color);
  }

  .theme-btn-grad {
    background: linear-gradient(135deg, var(--theme-color) 0%, #4facfe 100%);
    border: none;
    color: white;
    font-weight: 600;
    transition: all 0.3s;
  }
  .theme-btn-grad:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
    opacity: 0.95;
  }

  .event-card {
    border-radius: 16px;
    background: #fff;
    cursor: pointer;
  }

  .shadow-hover:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
    transform: translateY(-3px);
    border-color: var(--theme-color);
  }

  /* 富文本样式适配 */
  .rich-text :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1rem 0;
    box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  }

  .rich-text :deep(h1), .rich-text :deep(h2), .rich-text :deep(h3) {
    font-weight: bold;
    color: #2d3748;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .rich-text :deep(p) {
    color: #4a5568;
    line-height: 1.7;
    margin-bottom: 1rem;
  }

  /* 登录毛玻璃弹窗 */
  .glass-dialog :deep(.el-dialog) {
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(15px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 0 15px 35px rgba(0,0,0,0.1);
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in {
    animation: fadeIn 0.8s ease forwards;
  }

  /* 错误页背景与卡片样式 */
  .friendly-error-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 90vh;
    background-color: #f7f9fc;
    padding: 20px;
  }
  .error-card {
    background: #ffffff;
    max-width: 480px;
    width: 100%;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
    border: 1px solid #eef2f6;
    padding: 40px;
    text-align: center;
  }
  .icon-warning {
    font-size: 64px;
    color: #f56c6c;
    margin-bottom: 20px;
  }
  .error-title {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 12px;
  }
  .error-message {
    font-size: 15px;
    color: #5c6b73;
    line-height: 1.6;
    margin-bottom: 8px;
  }
  .error-sub-message {
    font-size: 13px;
    color: #9ba4b0;
    line-height: 1.5;
    margin-bottom: 30px;
  }
  .back-home-btn {
    display: inline-block;
    background: linear-gradient(135deg, var(--theme-color, #1890ff) 0%, #4facfe 100%);
    color: white !important;
    font-weight: 600;
    padding: 12px 28px;
    border-radius: 24px;
    text-decoration: none;
    transition: all 0.3s;
  }
  .back-home-btn:hover {
    box-shadow: 0 4px 15px rgba(24, 144, 255, 0.3);
    transform: translateY(-1px);
    opacity: 0.95;
  }

  /* 职位列表特定样式 */
  .filter-panel {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(226, 232, 240, 0.8);
    margin-bottom: 32px;
  }
  .filter-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }
  .filter-items {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
  }
  .filter-item {
    display: flex;
    align-items: center;
  }
  .filter-label {
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    margin-right: 10px;
  }
  .jobs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(540px, 1fr));
    gap: 24px;
  }
  @media (max-width: 768px) {
    .grid-layout {
      grid-template-columns: 1fr;
    }
    .jobs-grid {
      grid-template-columns: 1fr;
    }
  }
  .job-card {
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid rgba(226, 232, 240, 0.8);
    padding: 24px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 240px;
  }
  .job-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.08), 0 8px 8px -5px rgba(0, 0, 0, 0.04);
    border-color: var(--theme-color);
  }
  .job-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }
  .job-title {
    font-size: 20px;
    font-weight: 700;
    color: #1a202c;
    margin: 0;
    transition: color 0.2s;
    cursor: pointer;
  }
  .job-title:hover {
    color: var(--theme-color);
  }
  .job-salary {
    font-size: 18px;
    font-weight: 700;
    color: var(--theme-color);
    white-space: nowrap;
  }
  .job-meta-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }
  .job-desc-excerpt {
    font-size: 14px;
    color: #718096;
    line-height: 1.6;
    margin-bottom: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .job-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #edf2f7;
    padding-top: 16px;
    margin-top: auto;
  }
  .publish-time {
    font-size: 12px;
    color: #a0aec0;
  }

  /* Tailwind 兼容辅助类 */
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .justify-between { justify-content: space-between; }
  .items-center { align-items: center; }
  .flex-grow { flex-grow: 1; }
  .space-x-2 > * + * { margin-left: 8px; }
  .space-y-4 > * + * { margin-top: 16px; }
  .w-full { width: 100%; }
  .w-32 { width: 128px; }
  .mt-6 { margin-top: 24px; }
  .mb-2 { margin-bottom: 8px; }
  .mb-3 { margin-bottom: 12px; }
  .mb-4 { margin-bottom: 16px; }
  .mb-6 { margin-bottom: 24px; }
  .pb-4 { padding-bottom: 16px; }
  .border-b { border-bottom: 1px solid #edf2f7; }
  .mr-1 { margin-right: 4px; }
  .mr-2 { margin-right: 8px; }
  .ml-2 { margin-left: 8px; }
  .text-red-500 { color: #f56c6c; }
  .text-gray-800 { color: #2d3748; }
  .text-gray-600 { color: #4a5568; }
  .text-gray-500 { color: #718096; }
  .text-gray-400 { color: #cbd5e0; }
  .text-xs { font-size: 12px; }
  .text-sm { font-size: 14px; }
  .text-lg { font-size: 18px; }
  .text-2xl { font-size: 24px; }
  .font-bold { font-weight: 700; }
  .text-center { text-align: center; }
  .py-12 { padding-top: 48px; padding-bottom: 48px; }
  .py-24 { padding-top: 96px; padding-bottom: 96px; }
  .p-5 { padding: 20px; }
  .rounded-xl { border-radius: 12px; }
  .border { border: 1px solid #edf2f7; }
  .border-gray-100 { border-color: #f7fafc; }

  /* 职位详情与公告弹窗自定义样式 */
  .view-ann-detail-btn {
    margin-left: auto;
    font-weight: 600;
    color: var(--theme-color);
  }
  .job-detail-dialog :deep(.el-dialog), .announcement-detail-dialog :deep(.el-dialog) {
    border-radius: 20px;
    box-shadow: 0 15px 35px rgba(0,0,0,0.1);
  }
  .job-detail-container {
    padding: 10px 20px;
  }
  .job-detail-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .job-detail-name {
    font-size: 24px;
    font-weight: 800;
    color: #1a202c;
    margin: 0;
  }
  .job-detail-salary {
    font-size: 22px;
    font-weight: 700;
    color: var(--theme-color);
  }
  .job-detail-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 24px;
    border-bottom: 1px solid #edf2f7;
    padding-bottom: 16px;
  }
  .job-detail-section {
    margin-bottom: 24px;
  }
  .job-detail-sec-title {
    font-size: 16px;
    font-weight: 700;
    color: #2d3748;
    margin-top: 0;
    margin-bottom: 12px;
    border-left: 4px solid var(--theme-color);
    padding-left: 10px;
  }
  .job-detail-text {
    font-size: 14px;
    color: #4a5568;
    line-height: 1.8;
    white-space: pre-wrap;
    margin: 0;
  }
  .job-detail-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid #edf2f7;
    padding-top: 16px;
    margin-top: 32px;
  }
  .announcement-detail-container {
    padding: 10px 20px;
    max-height: 550px;
    overflow-y: auto;
  }
</style>
